import axios from "axios"

import store from "src/store"
import { getNewAccessToken } from "src/store/user/"
import { logoutUser } from "src/store/auth/logout"
import { logMessage } from "src/logging"
import { loadFogPreview } from "src/utils/fogPreview"
import { retryAfterSeconds } from "src/utils/apiErrors"
import i18n from "src/i18n"

/* The page's own origin, always: one Django serves the API under /api/ and this bundle from the
   root, so a relative base is not only enough, it is the only one that cannot go stale — no
   rebuild is needed to move the app to another domain. The split local run looks the same from
   here, because CRA's dev server proxies /api to Django (src/setupProxy.js). */
const backendUrl = "/api"

/* Whether a failed token refresh means the session itself is dead. SimpleJWT answers 401 to an
   expired, blacklisted or malformed refresh token; anything else — 5xx, a network error (status
   null) — is the server's problem, not the session's. Exported for the test. */
export const sessionIsInvalid = (refreshFailure) => refreshFailure?.status === 401

/* A throttled request (gamekit 0.11.0: sign-in, registration, the reset, joining a room, orders… each
   has a rate; 429 with DRF's English `detail`). Said in the player's language, with the wait the
   server gave. Exported for the test. */
export const throttledSentence = (response) => {
  const seconds = retryAfterSeconds(response)
  return seconds == null ? i18n.t("api.throttledNoTime") : i18n.t("api.throttled", { seconds })
}

const apiClient = axios.create({
  baseURL: backendUrl,
  headers: {
    "Content-Type": "application/json",
    // ngrok's free tier answers browser-looking requests with an HTML warning page instead of
    // the API unless this header rides along. Harmless everywhere else.
    "ngrok-skip-browser-warning": "true",
  },
})

// Add a request interceptor
apiClient.interceptors.request.use(
  function (config) {
    const accessToken = store.getState().user.accessToken
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`
    }
    // Tester's fog preview (see utils/fogPreview): the bootstrap payload must match the socket's
    // view, and the server redacts per request when asked. Ignored by a server with fog on.
    if (loadFogPreview()) {
      config.headers["X-Fog-Preview"] = "1"
    }
    console.log(
      `Sending request [${config.method}]: ${backendUrl}${config.url}. Payload: ${
        config.data ? JSON.stringify(config.data) : "None"
      }`
    )
    return config
  },
  function (error) {
    console.log(`Error sending request: ${error}`)
    return Promise.reject(error)
  }
)

// Add a response interceptor
apiClient.interceptors.response.use(
  function (response) {
    return response
  },
  async function (error) {
    const originalRequest = error.config
    if (
      // The endpoints that *hand out* a session cannot have an expired one. `/auth/google/` answers
      // 401 when Google's token does not verify; unlisted, that reads as "the session expired", so
      // the client refreshes a token it has not got and logs the player out of an account they were
      // in the middle of signing in to.
      // `/auth/guest/` hands one out too (2026-10-02), unauthenticated on purpose.
      !["/token/", "/token/refresh/", "/auth/google/", "/auth/guest/"].includes(originalRequest?.url) &&
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true
      try {
        const { accessToken } = await store.dispatch(getNewAccessToken()).unwrap()
        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`
        return apiClient(originalRequest)
      } catch (refreshFailure) {
        // Only a refresh the server *rejected* means the session is over. A refresh that never got
        // an answer, or got a 5xx, says nothing about the session — logging the player out for a
        // server hiccup (a deploy, a dev-server reload) threw away a perfectly good login.
        if (sessionIsInvalid(refreshFailure)) {
          logMessage({ text: i18n.t("api.sessionExpired"), type: "error" })
          store.dispatch(logoutUser())
        } else {
          logMessage({ text: i18n.t("api.error", { message: error.message || i18n.t("api.somethingWentWrong") }), type: "error" })
        }
        return Promise.reject(error)
      }
    }

    // A throttle is not a refusal of what the player did, only of how often: the sentence replaces
    // DRF's English `detail`, so a form that prints the answer (`handlesOwnErrors`) prints this one,
    // and anybody else gets it as the toast instead of axios's "status code 429".
    if (error.response?.status === 429) {
      const text = throttledSentence(error.response)
      error.response.data = { ...(typeof error.response.data === "object" ? error.response.data : {}), detail: text }
      if (!originalRequest?.handlesOwnErrors) logMessage({ text, type: "warning" })
      return Promise.reject(error)
    }

    // `handlesOwnErrors` on the request opts out of the toast below. The sentence it produces is
    // axios's own ("Request failed with status code 409") — never in the player's language, never
    // about what they did — so a screen that renders the server's refusal properly would otherwise
    // show that one beside it, in English, saying less. The caller still gets the rejection.
    if (!originalRequest?.handlesOwnErrors) {
      logMessage({
        text: i18n.t("api.error", { message: error.message || i18n.t("api.somethingWentWrong") }),
        type: "error",
      })
    }
    return Promise.reject(error)
  }
)

export default apiClient
